/**
 * installChecklist — the job packet's Install Order + Rebate Test Form on the
 * installer's Wrap Up (Seth 2026-09-25: "Incorporate the checklist as part of
 * the app closing process naturally and with what we already have … If a rebate
 * is added to a job and it's a cooling rebate then the rebate form should be
 * added for the installers to fill out").
 *
 *   · InstallChecklistCard — the Install Order's lines that fit this job's
 *     equipment, Yes / No / N/A like the paper. Lines 22–24 (line set, leak
 *     test, microns) show from Log the Charge. The paper's comments, "notify
 *     office" and signatures are the notes, Install Complete and the Sign-Off.
 *   · RebateTestCard — only on a cooling-rebate job: the Rebate Test Form in
 *     the sheet's numbering; Pegasus fills the customer, models, dates and does
 *     the arithmetic.
 * Data: features/jobs/installChecklist.ts + rebateTest.ts.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ClipboardCheck, FileText, Loader2, Thermometer } from "lucide-react";
import { cn } from "@/lib/cn";
import { haptic } from "@/native/boot";
import { InfoTip } from "@/components/InfoTip";
import { useJobEquipment, type Job } from "@/features/jobs/jobs";
import {
  useInstallCheckItems, useJobChecks, useSetJobCheck, checklistFor, autoAnswers, checklistProgress, YNNA,
  type InstallCheckItem,
} from "@/features/jobs/installChecklist";
import { useJobRefrigerant } from "@/features/jobs/refrigerant";
import {
  HEADER_FIELDS, SIZING_FIELDS, AIRFLOW_FIELDS, TXV_FIELDS, PISTON_FIELDS, DUCT_METHODS,
  rebateCompute, rebateMissing, rebateCounts, useRebateTestStatus, useRebateTest, useSaveRebateTest, useLastTechId, outsideTempNow,
  type RebateData, type RebateField,
} from "@/features/jobs/rebateTest";
import { Sheet, card, input } from "./installWork";

/* ------------------------------------------------------------ checklist */

export function InstallChecklistCard({ job, empno }: { job: Job; empno: string | null }) {
  const { data: items } = useInstallCheckItems();
  const { data: equip } = useJobEquipment(job.id);
  const { data: answers } = useJobChecks(job.id);
  const { data: logs } = useJobRefrigerant(job.id);
  const setCheck = useSetJobCheck();
  const lines = useMemo(() => checklistFor(items ?? [], (equip ?? []).filter((e) => e.kind !== "material")), [items, equip]);
  const auto = useMemo(() => autoAnswers(logs ?? []), [logs]);
  const p = checklistProgress(lines, answers, auto);
  const charge = (logs ?? []).find((l) => l.kind === "charge");
  const refrigerantJob = (logs ?? []).some((l) => l.kind === "charge") || (equip ?? []).some((e) => ["ac", "heat-pump", "mini-split", "coil"].includes(e.equip_key ?? ""));
  if (!items || lines.length === 0) return null;

  const put = (it: InstallCheckItem, value: string | null, note?: string | null) => {
    haptic("light");
    setCheck.mutate({ jobId: job.id, key: it.key, value, note: note ?? null, empno });
  };

  return (
    <section className={card}>
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-slate-400" />
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Install Checklist</h4>
        <InfoTip label="Where this list comes from">The Install Order from the job packet. Only the lines for the equipment on this job show; the office edits the list in Settings → Install Checklist. A No goes to the office with your note.</InfoTip>
        <span className={cn("m-chip ml-auto", p.answered === p.total && "m-chip-good")}>{p.answered} of {p.total}</span>
      </div>
      <ul className="mt-1.5 divide-y divide-slate-100">
        {lines.map((it) => (
          <ChecklistRow key={it.key} it={it} value={p.valueOf(it.key)} note={answers?.get(it.key)?.note ?? null}
            autoFrom={!answers?.has(it.key) ? auto.get(it.key)?.from ?? null : null}
            onPut={(v, note) => put(it, v, note)} />
        ))}
      </ul>
      {refrigerantJob && (
        <div className="mt-2 rounded-2xl bg-slate-50 px-3 py-2.5 text-[12.5px] text-slate-600">
          <div className="font-bold text-slate-700">From Log the Charge</div>
          <div className="mt-0.5">Line set soldered by: <b className="text-slate-800">{charge?.brazed_by ?? "—"}</b></div>
          <div>Leak test: <b className="text-slate-800">{charge?.leak_test_psi != null ? `${charge.leak_test_psi} psi` : "—"}</b> · Vacuum: <b className="text-slate-800">{charge?.vacuum_microns != null ? `${charge.vacuum_microns} microns` : "—"}</b></div>
          {!charge && <div className="mt-0.5 text-slate-500">Log the charge on the Equipment step.</div>}
        </div>
      )}
      {setCheck.isError && <p className="mt-2 text-center text-[12px] font-semibold text-rose-600">An answer didn&apos;t save — check the signal and tap it again.</p>}
    </section>
  );
}

function ChecklistRow({ it, value, note, autoFrom, onPut }: {
  it: InstallCheckItem; value: string | null; note: string | null; autoFrom: string | null;
  onPut: (v: string | null, note?: string | null) => void;
}) {
  const [text, setText] = useState(value && it.kind !== "ynna" ? value : "");
  const [why, setWhy] = useState(note ?? "");
  useEffect(() => { if (it.kind !== "ynna") setText(value ?? ""); }, [value, it.kind]);
  useEffect(() => { setWhy(note ?? ""); }, [note]);
  if (it.kind !== "ynna") {
    return (
      <li className="py-2">
        <label className="block">
          <span className="text-[13.5px] font-semibold text-slate-800">{it.label}{it.unit ? ` (${it.unit})` : ""}</span>
          <input value={text} inputMode={it.kind === "number" ? "decimal" : undefined}
            onChange={(e) => setText(it.kind === "number" ? e.target.value.replace(/[^0-9.\-]/g, "") : e.target.value)}
            onBlur={() => { if ((text.trim() || null) !== (value ?? null)) onPut(text.trim() || null); }}
            className={cn(input, "mt-1")} />
        </label>
      </li>
    );
  }
  return (
    <li className="py-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[13.5px] font-semibold leading-snug text-slate-800">{it.label}</span>
        <div className="flex shrink-0 gap-1" role="group" aria-label={it.label}>
          {YNNA.map((o) => (
            <button key={o.v} type="button" aria-pressed={value === o.v}
              onClick={() => onPut(value === o.v ? null : o.v, o.v === "no" ? why : null)}
              className={cn("h-9 min-w-[44px] rounded-xl px-2 text-[12.5px] font-bold [touch-action:manipulation]",
                value === o.v
                  ? (o.v === "yes" ? "bg-emerald-600 text-white" : o.v === "no" ? "bg-amber-500 text-white" : "bg-slate-700 text-white")
                  : "bg-slate-100 text-slate-600 active:bg-slate-200")}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      {autoFrom && value && <p className="mt-1 text-[11.5px] text-slate-500">{autoFrom}</p>}
      {value === "no" && (
        <input value={why} onChange={(e) => setWhy(e.target.value)} onBlur={() => { if (why.trim() !== (note ?? "")) onPut("no", why); }}
          placeholder="What's left? (goes to the office)" className={cn(input, "mt-1.5 !py-2 text-[13.5px]")} />
      )}
    </li>
  );
}

/* ------------------------------------------------------ rebate test form */

export function RebateTestCard({ job, empno, techFirst }: { job: Job; empno: string | null; techFirst: string | null }) {
  const { data: status } = useRebateTestStatus(job.id);
  const { data: row } = useRebateTest(job.id);
  const [open, setOpen] = useState(false);
  if (!status?.needed) return null;
  const c = rebateCounts(row?.data ?? {});
  const done = !!row?.completed_at;
  return (
    <section className={card}>
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-slate-400" />
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Rebate Test Form</h4>
        <InfoTip label="Why this job has it">{status.reason} The office sends it in with the rebate.</InfoTip>
        <span className={cn("m-chip ml-auto", done && "m-chip-good")}>{done ? "Done" : `${c.done} of ${c.total}`}</span>
      </div>
      <button type="button" onClick={() => setOpen(true)} className={cn("m-btn m-btn-big mt-3 w-full", done ? "m-btn-soft" : "m-btn-tint")}>
        <Thermometer className="h-[18px] w-[18px]" /> {done ? "Look at the Form" : row ? "Keep Going" : "Fill Out the Form"}
      </button>
      {open && <RebateTestSheet job={job} empno={empno} techFirst={techFirst} onClose={() => setOpen(false)} />}
    </section>
  );
}

const todayIso = () => new Date().toLocaleDateString("en-CA");

function RebateTestSheet({ job, empno, techFirst, onClose }: { job: Job; empno: string | null; techFirst: string | null; onClose: () => void }) {
  const { data: row, isLoading } = useRebateTest(job.id);
  const { data: equip } = useJobEquipment(job.id);
  const { data: logs, isLoading: logsLoading } = useJobRefrigerant(job.id);
  const { data: lastId } = useLastTechId(empno);
  const save = useSaveRebateTest();
  const [d, setD] = useState<RebateData | null>(null);
  const dirty = useRef(false);

  // Start from what's saved, else what Pegasus knows — including what the crew
  // already put on Log the Charge (TXV or piston, the targets), so nothing is typed twice.
  useEffect(() => {
    if (isLoading || logsLoading || d) return;
    const charges = (logs ?? []).filter((l) => l.kind === "charge");
    const c = charges.length === 1 ? charges[0] : null;
    const fromCharge: RebateData = {};
    if (c?.metering) {
      fromCharge.metering = c.metering;
      if (c.metering === "txv" && c.subcool_target != null) fromCharge.subcool_target = c.subcool_target;
      if (c.metering === "piston") {
        if (c.superheat_actual != null) fromCharge.superheat_actual = c.superheat_actual;
        if (c.superheat_target != null) fromCharge.superheat_target = c.superheat_target;
      }
    }
    setD({ test_date: todayIso(), tech_name: techFirst ?? "", ...fromCharge, ...(row?.data ?? {}) });
  }, [isLoading, logsLoading, logs, row, d, techFirst]);
  useEffect(() => {
    if (!d || d.tech_id || !lastId) return;
    setD((x) => (x && !x.tech_id ? { ...x, tech_id: lastId } : x));
  }, [lastId, d]);
  useEffect(() => {
    if (!d || d.outside_temp != null && d.outside_temp !== "") return;
    let live = true;
    void outsideTempNow().then((t) => { if (live && t != null) setD((x) => (x && (x.outside_temp == null || x.outside_temp === "") ? { ...x, outside_temp: t } : x)); });
    return () => { live = false; };
    // only on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d == null]);

  const cool = (equip ?? []).find((e) => ["ac", "heat-pump", "mini-split"].includes(e.equip_key ?? ""));
  const furn = (equip ?? []).find((e) => ["furnace", "air-handler"].includes(e.equip_key ?? ""));
  if (!d) return <Sheet title="Rebate Test Form" onClose={onClose}><div className="h-40 animate-pulse rounded-3xl bg-slate-200/60" /></Sheet>;

  const set = (k: string, v: string | number | null) => { dirty.current = true; setD((x) => ({ ...(x ?? {}), [k]: v })); };
  const comp = rebateCompute(d);
  const missing = rebateMissing(d);
  const close = () => {
    if (dirty.current) save.mutate({ jobId: job.id, data: d, complete: !!row?.completed_at && missing.length === 0, empno });
    onClose();
  };
  const done = () => save.mutate({ jobId: job.id, data: d, complete: true, empno }, { onSuccess: () => { haptic("success"); onClose(); } });

  const numField = (f: RebateField) => {
    if (f.kind === "computed") {
      const v = (comp as Record<string, number | null>)[f.key];
      return (
        <div key={f.key} className="flex items-center gap-2 py-1.5">
          <span className="w-8 shrink-0 text-right text-[12px] font-bold text-slate-400">{f.n}</span>
          <span className="min-w-0 flex-1 text-[13px] font-semibold text-slate-600">{f.label}</span>
          <span className="w-24 rounded-xl bg-slate-100 px-3 py-2 text-right text-[15px] font-bold tabular-nums text-slate-800">{v == null ? "—" : `${v}${f.unit?.startsWith("°") ? "°" : ""}`}</span>
        </div>
      );
    }
    if (f.kind === "choice") {
      return (
        <div key={f.key} className="py-1.5">
          <div className="flex items-center gap-2">
            <span className="w-8 shrink-0 text-right text-[12px] font-bold text-slate-400">{f.n}</span>
            <span className="min-w-0 flex-1 text-[13px] font-semibold text-slate-800">{f.label}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5 pl-10">
            {(f.options ?? []).map((o) => (
              <button key={o.v} type="button" aria-pressed={d[f.key] === o.v} onClick={() => set(f.key, d[f.key] === o.v ? null : o.v)}
                className={cn("h-9 min-w-[48px] rounded-xl px-3 text-[13px] font-bold", d[f.key] === o.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700")}>{o.label}</button>
            ))}
          </div>
        </div>
      );
    }
    const id = `rb-${f.key}`;
    return (
      <label key={f.key} htmlFor={id} className="flex items-center gap-2 py-1.5">
        <span className="w-8 shrink-0 text-right text-[12px] font-bold text-slate-400">{f.n}</span>
        <span className="min-w-0 flex-1 text-[13px] font-semibold text-slate-800">{f.label}{f.unit ? <span className="font-medium text-slate-400"> {f.unit}</span> : null}</span>
        <input id={id} type={f.kind === "date" ? "date" : "text"} inputMode={f.kind === "number" ? "decimal" : undefined}
          value={d[f.key] == null ? "" : String(d[f.key])}
          onChange={(e) => set(f.key, f.kind === "number" ? e.target.value.replace(/[^0-9.\-]/g, "") : e.target.value)}
          className={cn("w-28 rounded-xl border border-slate-200 bg-white px-3 py-2 text-right text-[15px] font-semibold tabular-nums text-slate-900 focus:border-slate-400 focus:outline-none", f.kind !== "number" && "text-left")} />
      </label>
    );
  };

  const block = (title: string, fields: RebateField[], note?: string) => (
    <section className={card}>
      <h4 className="text-[13px] font-extrabold uppercase tracking-[0.08em] text-slate-500">{title}</h4>
      {note && <p className="mt-0.5 text-[12px] text-slate-500">{note}</p>}
      <div className="mt-1 divide-y divide-slate-100">{fields.map(numField)}</div>
    </section>
  );

  return (
    <Sheet title="Rebate Test Form" sub={[job.customer_name, job.address].filter(Boolean).join(" · ")} onClose={close}
      footer={
        <div className="grid gap-2">
          <button type="button" disabled={missing.length > 0 || save.isPending} onClick={done} className="m-btn m-btn-good m-btn-big w-full">
            {save.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />}
            {missing.length > 0 ? `${missing.length} still blank` : "Done — Form Complete"}
          </button>
        </div>
      }>
      <section className={card}>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px]">
          <span className="text-slate-500">Name</span><span className="font-semibold text-slate-800">{job.customer_name ?? "—"}</span>
          <span className="text-slate-500">Address</span><span className="font-semibold text-slate-800">{job.address ?? "—"}</span>
          <span className="text-slate-500">AC model</span><span className="font-mono font-semibold text-slate-800">{cool?.model ?? "—"}</span>
          <span className="text-slate-500">Furnace model</span><span className="font-mono font-semibold text-slate-800">{furn?.model ?? "—"}</span>
        </div>
        <div className="mt-2 divide-y divide-slate-100">{HEADER_FIELDS.map(numField)}</div>
      </section>
      {block("Sizing", SIZING_FIELDS)}
      {block("Air Flow", AIRFLOW_FIELDS)}
      <section className={card}>
        <h4 className="text-[13px] font-extrabold uppercase tracking-[0.08em] text-slate-500">Metering Device</h4>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {([["txv", "TXV System"], ["piston", "Non-TXV System"]] as const).map(([v, label]) => (
            <button key={v} type="button" aria-pressed={d.metering === v} onClick={() => set("metering", v)}
              className={cn("h-11 rounded-2xl text-[14px] font-bold", d.metering === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700")}>{label}</button>
          ))}
        </div>
      </section>
      {d.metering === "txv" && block("TXV System", TXV_FIELDS)}
      {d.metering === "piston" && block("Non-TXV System", PISTON_FIELDS)}
      <section className={card}>
        <h4 className="text-[13px] font-extrabold uppercase tracking-[0.08em] text-slate-500">Ductwork — Which Method Was Used?</h4>
        <div className="mt-2 grid gap-1.5">
          {DUCT_METHODS.map((m) => (
            <button key={m.v} type="button" aria-pressed={d.duct_method === m.v} onClick={() => set("duct_method", m.v)}
              className={cn("flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-left text-[13px] font-semibold",
                d.duct_method === m.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700")}>
              <span className={cn("text-[12px] font-bold", d.duct_method === m.v ? "text-white/70" : "text-slate-400")}>{m.n}</span> {m.label}
            </button>
          ))}
        </div>
      </section>
      {missing.length > 0 && (
        <p className="px-1 text-[12px] text-slate-500">Still blank: {missing.join(" · ")}</p>
      )}
      {save.isError && <p className="text-center text-[12px] font-semibold text-rose-600">Didn&apos;t save — check the signal and try again.</p>}
    </Sheet>
  );
}

/** For Ready to Finish: the form's state on this job (null = not needed). */
export function useRebateTestReadiness(jobId: string) {
  const { data: status } = useRebateTestStatus(jobId);
  const { data: row } = useRebateTest(jobId);
  if (!status?.needed) return null;
  return { done: !!row?.completed_at, counts: rebateCounts(row?.data ?? {}) };
}

